/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ControllerFunctionProp } from 'componentregistry';
import { useControllerFunctions } from 'controller/ControllerRegistry';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import React, { memo } from 'react';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  select: { flex: 1 },
  tip: { marginBottom: 8, marginLeft: 8 },
});

function ControllerFunctionProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<ControllerFunctionProp, string>) {
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (alias: string) => {
    if (alias !== value) {
      onChange(property, alias);
    }
  };

  const functions = useControllerFunctions(appUid, pageId);

  const options = functions.map((fn) => ({ value: fn }));
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      data-testid={property}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'stretch',
          width: '100%',
        }}
      >
        <VirtualizedSelectInput
          disabled={isViewOnlyUser}
          className={classes.select}
          value={value ?? ''}
          options={options}
          onChange={handleChange}
          disableClearable={required}
          label={def.label}
          variant="standard"
        />
        {helpText ? (
          <Icon
            className={classes.tip}
            data-tip-at="top"
            data-tip-delay={0}
            data-tip={helpText}
            data-tip-markdown
            data-tip-width={600}
            iconName="comment-exclamation"
          />
        ) : null}
      </Box>
    </FormControl>
  );
}

export default memo(ControllerFunctionProperty);
